import React, { useState, useContext, useEffect } from 'react';
import ModalBtn from '../../molecules/modalButton/view';
import { Col, Form } from 'react-bootstrap';
import { Calendar, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { roleContext } from '../../../context/role/context';
import { saveDailyClosure, generateAccountingReport } from '../../../usecases/accounting-reports-usecase';
import { printDailyClosure } from '../../../utils/receipt-utils';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import ButtonLoading from '../../atoms/buttonLoading/view';
import ButtonBasic from '../../atoms/buttonBasic/view';
import './style.css';

const DailyClosureModal = ({ id = 'daily-closure-modal' }) => {
  const { profile } = useContext(roleContext);

  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [date, setDate] = useState(getTodayDateString());
  const [initialBalance, setInitialBalance] = useState(0);
  const [observations, setObservations] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchPreview = async (selectedDate) => {
    if (!selectedDate) return;
    setLoadingPreview(true);
    try {
      const res = await generateAccountingReport({
        report_code: 'daily_closure',
        date_from: selectedDate,
        date_to: selectedDate,
      });
      const previewData = res?.data || res;
      setPreview(previewData);

      if (previewData?.totals?.saldo_inicial !== undefined) {
        setInitialBalance(previewData.totals.saldo_inicial);
      }
    } catch (error) {
      console.error('Error fetching daily closure preview:', error);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleOpen = () => {
    const today = getTodayDateString();
    setDate(today);
    fetchPreview(today);
  };

  const handleSubmit = async () => {
    if (
      initialBalance === '' ||
      isNaN(parseFloat(initialBalance)) ||
      parseFloat(initialBalance) < 0
    ) {
      dispatchCustomEvent('popup', {
        type: 'error',
        message:
          'Por favor ingrese un saldo inicial válido y mayor o igual a cero.',
      });
      dispatchCustomEvent('loading-btn-submit-closure', {});
      return;
    }

    try {
      const data = {
        date_from: date,
        date_to: date,
        initial_balance: parseFloat(initialBalance),
        observations: observations || '',
      };

      const result = await saveDailyClosure(data);

      dispatchCustomEvent('popup', {
        type: 'success',
        message: 'Cierre de caja guardado exitosamente',
      });

      // Imprimir la tirilla
      printDailyClosure(result.data || result, profile);

      // Cerrar modal
      dispatchCustomEvent('close-modal', {});

      // Resetear campos
      setInitialBalance(0);
      setObservations('');
      setPreview(null);
    } catch (error) {
      console.error('Error saving daily closure:', error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Error al guardar el cierre de caja';
      dispatchCustomEvent('popup', {
        type: 'error',
        message: errorMsg,
      });
    } finally {
      dispatchCustomEvent('loading-btn-submit-closure', {});
    }
  };

  const calculatedInitial = preview?.totals?.saldo_inicial || 0;
  const initialDiff = parseFloat(initialBalance || 0) - calculatedInitial;

  return (
    <ModalBtn
      id={id}
      title="Cierre de Caja"
      icon={<Calendar size={20} />}
      onClick={handleOpen}
    >
      <div className="daily-closure-content">
        <Form action="javascript:void(0);" id="daily-closure-form">
          {/* Campos del formulario en columna */}
          <Form.Group className="mb-3">
            <label className="daily-closure-label">Fecha del Cierre</label>
            <Form.Control
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                fetchPreview(e.target.value);
              }}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <label className="daily-closure-label">
              Saldo Inicial de Caja ($)
            </label>
            <Form.Control
              type="number"
              min="0"
              step="any"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              placeholder="Ingrese el saldo inicial de efectivo"
              required
            />
          </Form.Group>

          {/* Comparación de Saldo (Alerta o Éxito) */}
          {preview && !loadingPreview && (
            <div className="mb-3">
              {initialDiff === 0 ? (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2 px-3 small border-0 bg-opacity-10 text-success">
                  <CheckCircle2 size={16} /> El saldo inicial coincide perfectamente con el teórico.
                </div>
              ) : (
                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 small border-0 bg-opacity-10 text-warning">
                  <AlertTriangle size={16} /> Diferencia de saldo inicial: ${Math.round(initialDiff).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Estado de carga de la vista previa */}
          {loadingPreview && (
            <div className="daily-closure-preview-loading">
              <RefreshCw className="spinner" size={16} /> Cargando resumen teórico...
            </div>
          )}

          {/* Tarjeta de totales teóricos */}
          {!loadingPreview && preview && preview.totals && (
            <div className="daily-closure-preview-card mb-3">
              <div className="preview-header">Resumen Teórico del Día</div>
              <div className="preview-grid">
                <div className="preview-item">
                  <span className="preview-label">Saldo Inicial Teórico:</span>
                  <span className="preview-val">${Math.round(preview.totals.saldo_inicial || 0).toLocaleString()}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Ventas:</span>
                  <span className="preview-val">${Math.round(preview.totals.total_ventas || 0).toLocaleString()}</span>
                </div>
                <div className="preview-item text-danger">
                  <span className="preview-label">Egresos Efectivo:</span>
                  <span className="preview-val">-${Math.round(preview.totals.egresos_efectivo || 0).toLocaleString()}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Otros Recibos:</span>
                  <span className="preview-val">+${Math.round(preview.totals.total_recibos || 0).toLocaleString()}</span>
                </div>
                <div className="preview-divider"></div>
                <div className="preview-item total-item">
                  <span className="preview-label">Saldo Final Calculado:</span>
                  <span className="preview-val">${Math.round(preview.totals.saldo_final || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Observaciones */}
          <Col>
            <Form.Group className="mb-3">
              <label className="daily-closure-label">Observaciones</label>
              <Form.Control
                as="textarea"
                rows={5}
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Ingrese novedades o comentarios sobre el cierre de caja (opcional)"
              />
            </Form.Group>
          </Col>
        </Form>

        <div className="daily-closure-footer">
          <ButtonBasic
            title="Cancelar"
            type="option"
            onClick={() => dispatchCustomEvent('close-modal', {})}
          />
          <ButtonLoading
            id="submit-closure"
            title="Generar Cierre"
            onClick={handleSubmit}
            className="btn-pill btn-pill-primary"
          />
        </div>
      </div>
    </ModalBtn>
  );
};

export default DailyClosureModal;
